import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';
import { useILScore } from './useILScore';
import { useMentalWellnessScore } from './useMentalWellnessScore';

export interface UnifiedWellnessScore {
  overall: number;
  trend: 'improving' | 'declining' | 'stable';
  breakdown: {
    physical: number;  // IL Score (inverted - lower is better)
    mental: number;     // Mental Wellness Score
    recovery: number;   // WHOOP Recovery (if available)
  };
  status: 'optimal' | 'good' | 'moderate' | 'attention-needed';
  message: string;
  recommendations: string[];
}

export function useUnifiedWellnessScore() {
  const { todayScore: ilScore, sevenDayAvg: ilAvg, isLoading: ilLoading } = useILScore(7);
  const { todayScore: mwScore, sevenDayAvg: mwAvg, isLoading: mwLoading } = useMentalWellnessScore(7);

  const { data: whoopData, isLoading: whoopLoading } = useQuery({
    queryKey: ['whoop-recovery-today'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const today = format(new Date(), 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('whoop_recovery_data')
        .select('recovery_score, hrv_rmssd')
        .eq('user_id', user.id)
        .gte('recorded_at', today)
        .order('recorded_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    }
  });

  const { data: whoopHistory } = useQuery({
    queryKey: ['whoop-recovery-history'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const startDate = format(subDays(new Date(), 7), 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('whoop_recovery_data')
        .select('recovery_score')
        .eq('user_id', user.id)
        .gte('recorded_at', startDate)
        .order('recorded_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const calculateUnifiedScore = (): UnifiedWellnessScore | null => {
    if (!ilScore && !mwScore && !whoopData) return null;

    // Physical Score (IL inverted: 100 - IL score so lower inflammation = higher score)
    const physicalScore = ilScore ? Math.max(0, 100 - ilScore.il_score) : 50;
    
    // Mental Score (direct)
    const mentalScore = mwScore?.mental_wellness_score ?? 50;
    
    // Recovery Score (WHOOP if available, otherwise estimate from sleep)
    const recoveryScore = whoopData?.recovery_score ?? (ilScore?.sleep_score ? ilScore.sleep_score * 1.2 : 50);

    // Weighted calculation: Mental 40%, Physical 35%, Recovery 25%
    const overall = Math.round(
      (mentalScore * 0.40) + 
      (physicalScore * 0.35) + 
      (recoveryScore * 0.25)
    );

    // Calculate trend
    let trend: 'improving' | 'declining' | 'stable' = 'stable';
    if (ilAvg && mwAvg) {
      const prevPhysical = Math.max(0, 100 - ilAvg);
      const prevOverall = (mwAvg * 0.40) + (prevPhysical * 0.35) + (recoveryScore * 0.25);
      const diff = overall - prevOverall;
      if (diff > 5) trend = 'improving';
      else if (diff < -5) trend = 'declining';
    }

    // Status and messaging
    let status: UnifiedWellnessScore['status'];
    let message: string;
    let recommendations: string[];

    if (overall >= 80) {
      status = 'optimal';
      message = 'You\'re thriving! Your mind and body are in exceptional harmony.';
      recommendations = [
        'Maintain your current wellness practices',
        'Consider challenging yourself with new growth activities',
        'Share your success strategies with your future self journal'
      ];
    } else if (overall >= 65) {
      status = 'good';
      message = 'You\'re doing well. Minor optimizations could elevate your wellness further.';
      recommendations = [
        physicalScore < 70 ? 'Focus on reducing inflammation through whole foods' : '',
        mentalScore < 70 ? 'Prioritise 10 minutes of mindfulness daily' : '',
        recoveryScore < 70 ? 'Improve sleep quality with evening wind-down routine' : ''
      ].filter(Boolean);
    } else if (overall >= 50) {
      status = 'moderate';
      message = 'Your wellness needs attention. Small consistent changes will make a difference.';
      recommendations = [
        'Complete daily check-ins to identify patterns',
        physicalScore < mentalScore ? 'Focus on physical health: sleep, nutrition, movement' : 'Prioritise mental health: therapy, meditation, journaling',
        'Use Therappi for personalised guidance'
      ];
    } else {
      status = 'attention-needed';
      message = 'Multiple wellness areas need care. Let\'s create a recovery plan together.';
      recommendations = [
        'Schedule a Therappi session for personalised support',
        'Activate nervous system reset protocol',
        'Focus on one small improvement today',
        'Reach out to your support network'
      ];
    }

    return {
      overall,
      trend,
      breakdown: {
        physical: Math.round(physicalScore),
        mental: Math.round(mentalScore),
        recovery: Math.round(recoveryScore)
      },
      status,
      message,
      recommendations: recommendations.slice(0, 3)
    };
  };

  const score = calculateUnifiedScore();

  return {
    score,
    isLoading: ilLoading || mwLoading || whoopLoading,
    hasWhoopData: !!whoopData
  };
}
